/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { FC, useCallback, useRef, useState, MouseEvent } from "react";
import { DropdownMenu, DROPDOWN_STYLE, IDropdownMenuItem } from "../DropdownMenu";
import { FC_ANCHOR } from "../FloatingContainer";
import { Icon, ICON_SIZE } from "../Icon";
import { Tooltip } from "../Tooltip";
import { FieldWrapper } from "./FieldWrapper";
import { BEM } from "@adapptio/lib-components";

export interface IFieldTypeOption {
	value: string;
	icon: string;
	label: string;
}

export interface IFieldTypeProps {
	options: IFieldTypeOption[];
	value: string;
	className?: string;
	tooltip?: string;
	onChange?: (value: string) => void;
	inline?: boolean;
}

export const FieldType: FC<IFieldTypeProps> = ({ value, options, className, tooltip, onChange, inline }) => {
	const baseClass = BEM("edt-field-control", null, {
		inline
	});

	const [ isMenuOpened, setIsMenuOpened ] = useState(false);
	const controlElRef = useRef<HTMLButtonElement>();

	const handleButtonClick = useCallback((ev: MouseEvent) => {
		ev.stopPropagation();
		setIsMenuOpened((x) => !x);
	}, []);

	const handleItemClick = useCallback(
		(item: IDropdownMenuItem) => {
			if (onChange) {
				onChange(item.id);
			}
			setIsMenuOpened(false);
		},
		[ onChange ]
	);

	const handleMenuClose = useCallback(() => {
		setIsMenuOpened(false);
	}, []);

	let currentOption = options[0] || {
		value: null,
		icon: "mdi/help"
	};

	for (let i = 0; i < options.length; i++) {
		if (options[i].value === value) {
			currentOption = options[i];
			break;
		}
	}

	return (
		<FieldWrapper type="type" className={className}>
			<button className={baseClass.toClassName()} ref={controlElRef} onClick={handleButtonClick}>
				<Icon name={currentOption.icon} size={ICON_SIZE.XSMALL} />
				<DropdownMenu
					parent={controlElRef}
					items={options.map((item) => ({
						id: item.value,
						icon: item.icon,
						label: item.label,
						selected: item.value === value
					}))}
					anchor={FC_ANCHOR.LEFT_BOTTOM}
					style={DROPDOWN_STYLE.COMPACT}
					isOpened={isMenuOpened}
					onClose={handleMenuClose}
					onItemClick={handleItemClick}
				/>
				{tooltip ? (
					<Tooltip
						for={controlElRef}
						anchor={FC_ANCHOR.CENTER_TOP}
						openDelay={1200}
						disabled={isMenuOpened}
					>
						{tooltip}
					</Tooltip>
				) : null}
			</button>
		</FieldWrapper>
	);
};
